import React, { useState } from 'react';

import { ScopeLineItemDebug, ScopeDebugCandidate } from '../../types';

interface ScopeDebugDrawerProps {
  debug: ScopeLineItemDebug;
  /**
   * When true, sections reveal one at a time on click. When false, the
   * whole trace is rendered at once.
   */
  stepMode?: boolean;
}

const SECTIONS = [
  'activity',
  'candidates',
  'prompt',
  'response',
  'resolution',
  'validator',
] as const;

type SectionKey = (typeof SECTIONS)[number];

const SECTION_LABELS: Record<SectionKey, string> = {
  activity: '1. Activity extractor output',
  candidates: '2. Retrieval candidates',
  prompt: '3. Code-selection prompt',
  response: '4. LLM response',
  resolution: '5. Resolution',
  validator: '6. Validator findings',
};

function Mono({ children }: { children: React.ReactNode }) {
  return (
    <pre className="whitespace-pre-wrap break-words rounded border border-gray-200 bg-gray-50 p-2 font-mono text-[11px] leading-snug text-gray-800 overflow-x-auto max-h-96">
      {children}
    </pre>
  );
}

function json(value: unknown): string {
  if (value === null || value === undefined) return '(none)';
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

/**
 * Renders one labelled section. In step mode the body is hidden until the
 * caller has revealed the section; otherwise it's always visible.
 */
function Section({
  label,
  body,
  visible,
}: {
  label: string;
  body: React.ReactNode;
  visible: boolean;
}) {
  return (
    <div className="space-y-1">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-600">
        {label}
      </div>
      {visible ? body : <div className="text-xs text-gray-400">(hidden — click Next to reveal)</div>}
    </div>
  );
}

function CandidatesBlock({ candidates }: { candidates?: ScopeDebugCandidate[] }) {
  if (!candidates || candidates.length === 0) {
    return <div className="text-xs text-gray-500">No candidates retrieved.</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full font-mono text-[11px]">
        <thead className="text-left text-gray-600">
          <tr>
            <th className="pr-2">#</th>
            <th className="pr-2">cat sel</th>
            <th className="pr-2">score</th>
            <th className="pr-2">unit</th>
            <th className="pr-2">supported</th>
            <th>description</th>
          </tr>
        </thead>
        <tbody>
          {candidates.map((c, i) => (
            <tr key={`${c.cat}-${c.sel}-${i}`} className="border-t border-gray-200">
              <td className="pr-2 text-gray-500">{i + 1}</td>
              <td className="pr-2 text-primary">
                {c.cat} {c.sel}
              </td>
              <td className="pr-2">
                {c.relevance_score !== undefined ? c.relevance_score.toFixed(3) : '—'}
              </td>
              <td className="pr-2">{c.unit}</td>
              <td className="pr-2">{(c.supported_activities || []).join(' ')}</td>
              <td>{c.description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export const ScopeDebugDrawer: React.FC<ScopeDebugDrawerProps> = ({
  debug,
  stepMode = false,
}) => {
  // In step mode, track how far the user has clicked through.
  // 0 = nothing revealed, 6 = all revealed.
  const [stepIndex, setStepIndex] = useState(stepMode ? 0 : SECTIONS.length);

  // Reason: if stepMode prop flips off, snap to full reveal.
  React.useEffect(() => {
    if (!stepMode) setStepIndex(SECTIONS.length);
  }, [stepMode]);

  const isVisible = (key: SectionKey) => {
    const idx = SECTIONS.indexOf(key);
    return idx < stepIndex;
  };

  const skipped = debug.selection_skipped;
  const exception = debug.selection_exception;
  const hasPrompt = Boolean(debug.selection_prompt);

  return (
    <div className="mt-2 space-y-3 rounded border border-dashed border-purple-300 bg-purple-50/30 p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-purple-700">
          Pipeline trace · source={debug.source}
          {debug.activity_index !== undefined && debug.activity_index !== null
            ? ` · activity #${(debug.activity_index ?? 0) + 1}`
            : ''}
          {debug.selection_model ? ` · model=${debug.selection_model}` : ''}
        </div>
        {stepMode && stepIndex < SECTIONS.length && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setStepIndex((i) => Math.min(SECTIONS.length, i + 1));
            }}
            className="rounded border border-purple-300 bg-white px-2 py-0.5 text-[11px] font-medium text-purple-700 hover:bg-purple-100"
          >
            {stepIndex === 0
              ? 'Reveal activity →'
              : `Show ${SECTION_LABELS[SECTIONS[stepIndex]].replace(/^\d+\.\s/, '')} →`}
          </button>
        )}
        {stepMode && stepIndex >= SECTIONS.length && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setStepIndex(0);
            }}
            className="rounded border border-purple-300 bg-white px-2 py-0.5 text-[11px] font-medium text-purple-700 hover:bg-purple-100"
          >
            Reset
          </button>
        )}
      </div>

      {debug.note && (
        <div className="text-[11px] italic text-gray-600">{debug.note}</div>
      )}

      <Section
        label={SECTION_LABELS.activity}
        visible={isVisible('activity')}
        body={<Mono>{json(debug.activity_extractor_output)}</Mono>}
      />

      <Section
        label={`${SECTION_LABELS.candidates} (${debug.candidate_count ?? debug.candidates?.length ?? 0})`}
        visible={isVisible('candidates')}
        body={<CandidatesBlock candidates={debug.candidates} />}
      />

      <Section
        label={SECTION_LABELS.prompt}
        visible={isVisible('prompt')}
        body={
          skipped ? (
            <div className="text-xs text-amber-700">
              Selection LLM call was skipped: {debug.selection_skip_reason ?? 'unknown reason'}
            </div>
          ) : hasPrompt ? (
            <Mono>{debug.selection_prompt}</Mono>
          ) : (
            <div className="text-xs text-gray-500">No prompt recorded.</div>
          )
        }
      />

      <Section
        label={SECTION_LABELS.response}
        visible={isVisible('response')}
        body={
          exception ? (
            <div className="text-xs text-red-700">
              Selection raised exception: <code>{exception}</code>
            </div>
          ) : (
            <Mono>{json(debug.selection_response)}</Mono>
          )
        }
      />

      <Section
        label={SECTION_LABELS.resolution}
        visible={isVisible('resolution')}
        body={<Mono>{json(debug.resolution)}</Mono>}
      />

      <Section
        label={`${SECTION_LABELS.validator} (${debug.validator_findings?.length ?? 0})`}
        visible={isVisible('validator')}
        body={
          !debug.validator_findings || debug.validator_findings.length === 0 ? (
            <div className="text-xs text-gray-500">No validator findings for this code.</div>
          ) : (
            <ul className="space-y-1 text-xs">
              {debug.validator_findings.map((f, i) => (
                <li
                  key={i}
                  className={`rounded border px-2 py-1 ${
                    f.severity === 'error'
                      ? 'border-red-300 bg-red-50 text-red-800'
                      : f.severity === 'warning'
                        ? 'border-amber-300 bg-amber-50 text-amber-800'
                        : 'border-gray-300 bg-white text-gray-700'
                  }`}
                >
                  <span className="font-semibold uppercase">[{f.severity}]</span>{' '}
                  {f.message}
                  {f.suggestion && (
                    <div className="mt-0.5 text-[11px] italic">
                      Suggestion: {f.suggestion}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )
        }
      />
    </div>
  );
};
